import React, { useState } from 'react';
import { Button, Table, Popconfirm, Card, Input, Form, Modal, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import {
    useGetExternalConsultants,
    useCreateExternalConsultant,
    useUpdateExternalConsultant,
    useDeleteExternalConsultant,
} from '../../../hooks/useExternal';

const ExternalConsultantsManagement = () => {
    const [consultantForm] = Form.useForm();

    // State
    const [isConsultantModalVisible, setIsConsultantModalVisible] = useState(false);
    const [editingConsultant, setEditingConsultant] = useState(null);
    const [consultantSearchText, setConsultantSearchText] = useState('');

    // Queries and Mutations
    const { data: consultants = [], isLoading: consultantsLoading } = useGetExternalConsultants();
    const createConsultantMutation = useCreateExternalConsultant();
    const updateConsultantMutation = useUpdateExternalConsultant();
    const deleteConsultantMutation = useDeleteExternalConsultant();

    // Handlers
    const handleAddConsultant = () => {
        setEditingConsultant(null);
        consultantForm.resetFields();
        setIsConsultantModalVisible(true);
    };

    const handleEditConsultant = (consultant) => {
        setEditingConsultant(consultant);
        consultantForm.setFieldsValue({
            name: consultant.name,
            email: consultant.email,
            phone: consultant.phone,
            about: consultant.about || '',
        });
        setIsConsultantModalVisible(true);
    };

    const handleConsultantSubmit = async () => {
        try {
            const values = await consultantForm.validateFields();
            
            if (editingConsultant) {
                await updateConsultantMutation.mutateAsync({ 
                    id: editingConsultant.id, 
                    ...values 
                });
            } else {
                await createConsultantMutation.mutateAsync(values);
            }
            
            setIsConsultantModalVisible(false);
            consultantForm.resetFields();
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const handleDeleteConsultant = (id) => {
        deleteConsultantMutation.mutate(id);
    };

    // Filter consultants by search
    const filteredConsultants = consultants.filter(consultant =>
        consultant.name?.toLowerCase().includes(consultantSearchText.toLowerCase()) ||
        consultant.email?.toLowerCase().includes(consultantSearchText.toLowerCase()) ||
        consultant.phone?.toLowerCase().includes(consultantSearchText.toLowerCase())
    );

    // Consultant Columns
    const consultantColumns = [
        {
            title: <span className="text-white font-semibold">#</span>,
            dataIndex: 'id',
            width: 80,
            render: (id, record, index) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary_hover rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                </div>
            ),
        },
        {
            title: <span className="text-white font-semibold">Consultant Name</span>,
            dataIndex: 'name',
            render: (text) => (
                <Space>
                    <UserOutlined className="text-primary" />
                    <span className="font-semibold text-gray-800 text-base">{text}</span>
                </Space>
            ),
        },
        {
            title: <span className="text-white font-semibold">Email</span>,
            dataIndex: 'email',
            render: (text) => (
                <Space>
                    <MailOutlined className="text-gray-500" />
                    <span className="text-gray-700">{text}</span>
                </Space>
            ),
        },
        {
            title: <span className="text-white font-semibold">Phone</span>,
            dataIndex: 'phone',
            render: (text) => (
                <Space>
                    <PhoneOutlined className="text-gray-500" />
                    <span className="text-gray-700">{text}</span>
                </Space>
            ),
        },
        {
            title: <span className="text-white font-semibold">About</span>,
            dataIndex: 'about',
            render: (text) => (
                <Space>
                    <span className="text-gray-700">{text}</span>
                </Space>
            ),
        },
        {
            title: <span className="text-white font-semibold">Actions</span>,
            width: 180,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditConsultant(record)}
                        className="bg-primary text-white border-0 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
                    />
                    <Popconfirm
                        title={<span className="font-semibold">Delete Consultant?</span>}
                        description="Are you sure you want to delete this external consultant?"
                        onConfirm={() => handleDeleteConsultant(record.id)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true, className: 'shadow-md' }}
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            className="shadow-md hover:shadow-lg transition-all"
                            loading={deleteConsultantMutation.isPending}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-4 py-5">
            {/* Main Content Card */}
            <Card className="border-0 rounded-2xl overflow-hidden">
                <style>
                    {`  
                        .ant-table-thead > tr > th {
                            background: linear-gradient(135deg, #1F6F7D 0%, #1F6F7D 100%) !important;
                            color: white !important;
                            font-weight: 600 !important;
                            border: none !important;
                            padding: 16px !important;
                            font-size: 14px !important;
                        }
                        .ant-table-thead > tr > th::before {
                            display: none !important;
                        }
                        .ant-table-tbody > tr > td {
                            padding: 16px !important;
                            border-bottom: 1px solid #f0f0f0 !important;
                        }
                    `}
                </style>

                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <Input
                            placeholder="Search consultants by name, email, or phone..."
                            prefix={<SearchOutlined className="text-gray-400" />}
                            value={consultantSearchText}
                            onChange={e => setConsultantSearchText(e.target.value)}
                            size="large"
                            className="max-w-md shadow-md rounded-xl border-2 border-gray-200 hover:border-primary focus:border-primary transition-all"
                            allowClear
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddConsultant}
                            size="large"
                            className="bg-gradient-to-r from-primary to-primary_hover hover:from-primary_hover hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                        >
                            Add New Consultant
                        </Button>
                    </div>
                </div>

                <div className="rounded-xl overflow-hidden shadow-lg">
                    <Table
                        columns={consultantColumns}
                        dataSource={filteredConsultants}
                        loading={consultantsLoading}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showTotal: (total) => `Total ${total} consultants`,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50'],
                            className: 'px-4 py-4',
                        }}
                    />
                </div>
            </Card>

            {/* Consultant Modal */}
            <Modal
                title={
                    <div className="flex items-center gap-3 text-xl">
                        <UserOutlined className="text-primary" />
                        <span className="font-bold">{editingConsultant ? 'Edit External Consultant' : 'Add New External Consultant'}</span>
                    </div>
                }
                open={isConsultantModalVisible}
                onCancel={() => {
                    setIsConsultantModalVisible(false);
                    consultantForm.resetFields();
                }}
                onOk={handleConsultantSubmit}
                confirmLoading={createConsultantMutation.isPending || updateConsultantMutation.isPending}
                okText={editingConsultant ? 'Update Consultant' : 'Create Consultant'}
                cancelText="Cancel"
                okButtonProps={{
                    className: 'bg-gradient-to-r from-primary to-primary_hover border-0 shadow-md hover:shadow-lg',
                }}
                width={600}
                className="rounded-2xl"
            >
                <Form form={consultantForm} layout="vertical" className="mt-6">
                    <Form.Item
                        name="name"
                        label={<span className="font-semibold text-base">Consultant Name</span>}
                        rules={[{ required: true, message: 'Please enter consultant name' }]}
                    >
                        <Input 
                            placeholder="Enter consultant full name" 
                            size="large"
                            prefix={<UserOutlined className="text-gray-400" />}
                            className="rounded-xl"
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={<span className="font-semibold text-base">Email Address</span>}
                        rules={[
                            { required: true, message: 'Please enter email address' },
                            { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                    >
                        <Input 
                            placeholder="Enter email address" 
                            size="large"
                            prefix={<MailOutlined className="text-gray-400" />}
                            className="rounded-xl"
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={<span className="font-semibold text-base">Phone Number</span>}
                        rules={[
                            { required: true, message: 'Please enter phone number' },
                            { pattern: /^[0-9+\-\s()]+$/, message: 'Please enter a valid phone number' }
                        ]}
                    >
                        <Input 
                            placeholder="Enter phone number" 
                            size="large"
                            prefix={<PhoneOutlined className="text-gray-400" />}
                            className="rounded-xl"
                        />
                    </Form.Item>

                    <Form.Item
                        name="about"
                        label={<span className="font-semibold text-base">About (Optional)</span>}
                    >
                        <Input.TextArea 
                            placeholder="Enter additional information about the consultant" 
                            rows={4}
                            className="rounded-xl"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExternalConsultantsManagement;